import { Link } from "react-router-dom";
import { ArrowRight, Ear, Leaf, HeartHandshake, Home, GraduationCap } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, Overline } from "@/components/Reveal";
import { SITE, IMAGES } from "@/config/site";

const PILLARS = [
    { icon: Ear, title: "Ascolto empatico", text: "Prima delle mani, le parole: ogni sessione inizia da come stai davvero." },
    { icon: Leaf, title: "Natura", text: "Oli e tessuti naturali, scelti con attenzione per rispettare la pelle e l’ambiente." },
    { icon: HeartHandshake, title: "Approccio su misura", text: "Nessun protocollo rigido: il trattamento si adatta alla persona, mai il contrario." },
    { icon: Home, title: "Spazio protetto", text: "Un ambiente riservato e accogliente, dove puoi lasciare fuori tutto il resto." },
];

const ChiSono = () => (
    <div data-testid="chi-sono-page">
        <SEO
            title="Chi sono | Sara Solferini · Studio Olistico Bologna"
            description="Il benessere parte dall'ascolto. Scopri la filosofia e l'approccio dello studio olistico di Sara Solferini a Bologna."
        />

        <header className="mx-auto max-w-6xl px-5 pb-20 pt-36 md:px-8 md:pt-44">
            <div className="grid items-center gap-14 md:grid-cols-2 md:gap-20">
                <Reveal>
                    <div className="overflow-hidden rounded-2xl rounded-t-[12rem]">
                        <img
                            src={IMAGES.portrait}
                            alt={`${SITE.brand}, operatrice del benessere`}
                            className="h-auto w-full"
                            data-testid="chi-sono-portrait"
                        />
                    </div>
                </Reveal>
                <div>
                    <Reveal>
                        <Overline>Chi sono</Overline>
                        <h1 className="mt-5 font-serif text-4xl font-light leading-tight tracking-tight text-ink sm:text-5xl" data-testid="chi-sono-title">
                            Il benessere parte <span className="italic text-sage">dall’ascolto.</span>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-4 font-serif text-2xl italic text-bark" data-testid="chi-sono-name">
                            {SITE.brand} · operatrice del benessere
                        </p>
                        <p className="mt-7 text-base leading-relaxed text-clay">
                            Da sempre credo che il corpo custodisca una memoria silenziosa di ogni nostra
                            giornata: le tensioni accumulate, i respiri trattenuti, le emozioni non dette. La
                            mia passione nasce proprio da qui — dal desiderio di tradurre quel silenzio in
                            benessere.
                        </p>
                        <p className="mt-5 text-base leading-relaxed text-clay">
                            Con professionalità e ascolto profondo, uso le mani per sciogliere i nodi
                            dell’anima e restituire a chi mi si affida la propria armonia naturale. Ogni
                            trattamento è un incontro unico, modulato su di te, sul tuo respiro e su ciò di
                            cui il tuo corpo ha bisogno in quel momento.
                        </p>
                        <p className="mt-5 text-base leading-relaxed text-clay">
                            Per questo, nel cuore di Bologna, ho creato uno spazio dove il tempo
                            rallenta: una dimora di calore, luce morbida e silenzio accogliente, pensata per
                            farti tornare a te stesso.
                        </p>
                    </Reveal>
                </div>
            </div>
        </header>

        <section className="bg-sand py-20 md:py-28" data-testid="chi-sono-approccio">
            <div className="mx-auto max-w-6xl px-5 md:px-8">
                <Reveal>
                    <Overline>Il mio approccio</Overline>
                    <h2 className="mt-5 max-w-xl font-serif text-3xl font-normal tracking-tight text-ink sm:text-4xl">
                        Quattro radici, <span className="italic text-sage">un solo intento</span>
                    </h2>
                </Reveal>
                <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {PILLARS.map((p, i) => (
                        <Reveal key={p.title} delay={i * 0.08}>
                            <div className="h-full rounded-2xl bg-card p-7 shadow-[0_4px_20px_rgba(67,46,30,0.05)]" data-testid={`pillar-${i}`}>
                                <p.icon className="h-6 w-6 text-sage" strokeWidth={1.5} />
                                <h3 className="mt-4 font-serif text-xl text-ink">{p.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-clay">{p.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-20 md:px-8 md:py-28" data-testid="chi-sono-formazione">
            <div className="grid gap-12 md:grid-cols-2 md:gap-20">
                <Reveal>
                    <GraduationCap className="h-7 w-7 text-sage" strokeWidth={1.5} />
                    <h2 className="mt-5 font-serif text-3xl font-normal tracking-tight text-ink">Formazione</h2>
                    <ul className="mt-6 space-y-4" data-testid="formazione-list">
                        <li className="flex gap-4 rounded-2xl border border-border bg-card p-5" data-testid="formazione-item-diploma">
                            <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-sage" />
                            <div>
                                <p className="font-medium text-ink">Diploma Nazionale Biennale</p>
                                <p className="mt-1 text-sm leading-relaxed text-clay">
                                    Conseguito presso l’Accademia CSB · Centro Studio Benessere di Bologna
                                </p>
                            </div>
                        </li>
                    </ul>
                </Reveal>
                <Reveal delay={0.15}>
                    <HeartHandshake className="h-7 w-7 text-sage" strokeWidth={1.5} />
                    <h2 className="mt-5 font-serif text-3xl font-normal tracking-tight text-ink">Come lavoro</h2>
                    <p className="mt-5 text-base leading-relaxed text-clay">
                        Non prometto risultati miracolosi: i trattamenti non sostituiscono e non hanno
                        valenza medica, ma offrono presenza, competenza e un ambiente in cui il
                        corpo può davvero rilassarsi. Se stai cercando un momento autentico di pausa, qui lo
                        troverai.
                    </p>
                    <Link
                        to="/prenota"
                        data-testid="chi-sono-cta-prenota"
                        className="group mt-8 inline-flex items-center gap-2 rounded-full bg-sage px-8 py-3.5 text-sm font-semibold uppercase tracking-widest text-cream transition-colors duration-300 hover:bg-sage-dark"
                    >
                        Prenota un incontro
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                    </Link>
                </Reveal>
            </div>
        </section>
    </div>
);

export default ChiSono;
